import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { PaginationMeta } from "@/configs/request";

const SIZES = [20, 50, 100];

interface PaginationProps {
  meta: PaginationMeta | undefined;
  onPageChange: (page: number) => void;
  /**
   * Berilmasa — sahifa hajmi tanlovi umuman ko'rsatilmaydi.
   *
   * Sahifa ichidagi kichik jadvallar qat'iy hajm bilan ishlaydi:
   * tanlov qoldirilsa, `SIZES` da yo'q qiymatda select bo'sh
   * ko'rinardi va bosilganda jadval kutilmaganda 20 qatorga
   * kengayardi.
   */
  onLimitChange?: (limit: number) => void;
}

export function Pagination({ meta, onPageChange, onLimitChange }: PaginationProps) {
  const { t } = useTranslation();
  if (!meta || meta.total === 0) return null;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
      <p className="text-muted-foreground text-sm">{t("table.total", { count: meta.total })}</p>

      <div className="flex items-center gap-4">
        {onLimitChange ? (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground text-sm">{t("table.rowsPerPage")}</span>
            <Select
              value={String(meta.limit)}
              onValueChange={value => onLimitChange(Number(value))}
            >
              <SelectTrigger size="sm" className="w-[80px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SIZES.map(size => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            aria-label={t("table.previous")}
            disabled={meta.page <= 1}
            onClick={() => onPageChange(meta.page - 1)}
          >
            <ChevronLeft className="size-4" />
          </Button>
          <span className="px-2 text-sm tabular-nums">
            {meta.page} {t("table.of")} {meta.totalPages}
          </span>
          <Button
            variant="outline"
            size="icon"
            aria-label={t("table.next")}
            // `hasNext` ga qaraymiz, `page < totalPages` ga emas: oxirgi
            // sahifada yangi yozuv paydo bo'lsa, backend buni biladi.
            disabled={!meta.hasNext}
            onClick={() => onPageChange(meta.page + 1)}
          >
            <ChevronRight className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
